import { Drawer } from '@mantine/core'
import { Button } from './Button'
import { Colors } from '../style/colors'
import { GlobalFlyoutContext, CloseGuardContext, FlyoutContent, FlyoutHandler } from '../hooks/useGlobalFlyout'
import { CSSProperties, FC, FunctionComponent, ReactNode, useMemo, useRef, useState } from 'react'
import { Fonts } from '../style/fonts'
import { TrackingLabel } from '../types/tracking'

type AdditionalProps = object

const CLOSE_TIMEOUT_MS = 250

const NullComponent: FunctionComponent = () => null

export const Section = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <div
    className={`${className ?? ''}`}
    style={{
      display: 'flex',
      flexDirection: 'column',
      paddingTop: '0.5rem',
      paddingBottom: '0.5rem',
      ...Fonts.P1,
      color: Colors.Jet,
    }}
  >
    {children}
  </div>
)

export const FlyoutTitle: FC<{ children: React.ReactNode }> = ({ children }) => <div style={{ gridArea: 'title', ...Fonts.H1 }}>{children}</div>

export const FlyoutBody: FC<{ children: React.ReactNode }> = ({ children }) => <div style={{ gridArea: 'body', minHeight: 0, overflowY: 'auto' }}>{children}</div>

export const FlyoutActions: FC<{ children: React.ReactNode }> = ({ children }) => <div style={{ gridArea: 'actions', gridColumnStart: 'auto' }}>{children}</div>

const defaultContent: FlyoutContent = {
  Component: NullComponent,
  additionalProps: {},
  containerProps: { closeTrackingLabel: 'common-flyout-close' },
}

export const GlobalFlyoutWrapper: FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setOpen] = useState(false)
  const [content, setContent] = useState<FlyoutContent>(defaultContent)
  const closeGuardRef = useRef<(() => Promise<boolean> | boolean) | null>(null)

  const closeGuardContext = useMemo(() => ({ guardRef: closeGuardRef }), [])

  const handler = useMemo((): FlyoutHandler => {
    return {
      open: (comp, containerProps, additionalProps) => {
        setContent({
          Component: comp as unknown as FunctionComponent<AdditionalProps>,
          additionalProps,
          containerProps,
        })
        setOpen(true)
      },
      close: async () => {
        setOpen(false)
        return new Promise((resolve) =>
          setTimeout(() => {
            setContent(defaultContent)
            resolve()
          }, CLOSE_TIMEOUT_MS + 1)
        )
      },
    }
  }, [])

  const requestClose = async () => {
    const { onBeforeDefaultClose, onAfterDefaultClose } = content.containerProps || {}

    if (closeGuardRef.current) {
      const canClose = await closeGuardRef.current()
      if (!canClose) return
    }

    const shouldClose = onBeforeDefaultClose ? await onBeforeDefaultClose() : true

    if (shouldClose !== false) {
      await handler.close()
      onAfterDefaultClose?.()
    }
  }

  const { Component, additionalProps, containerProps } = content

  const color = containerProps?.color ?? Colors.King
  const altColor = containerProps?.altColor ?? Colors.Pool

  const drawerContentStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'hidden',
    borderLeft: '6px solid',
    borderImageSlice: 1,
    borderImageSource: `linear-gradient(180deg, ${color} 0%, ${altColor} 100%)`,
    boxShadow: '0 0 20px rgba(54, 54, 54, 0.5)',
  }

  const drawerBodyStyle: CSSProperties = {
    flex: 1,
    minHeight: 0,
    display: 'grid',
    gridTemplateAreas: `
      'title      title       headerClose'
      'body       body        body'
      'actionClose actions    actions'
    `,
    gridTemplateRows: 'auto 1fr auto',
    gridTemplateColumns: 'auto 1fr auto',
    gap: '12px',
    padding: '20px 20px 12px',
  }

  // 🔥 style global sans outline (appliqué directement sur les boutons)
  const noFocusStyle: CSSProperties = {
    outline: 'none',
  }

  return (
    <GlobalFlyoutContext.Provider value={handler}>
      <Drawer
        opened={isOpen}
        onClose={requestClose}
        position="right"
        size={containerProps?.width ?? '600px'}
        withCloseButton={false}
        overlayProps={{ backgroundOpacity: 0.1 }}
        styles={{
          content: drawerContentStyle,
          body: drawerBodyStyle,
        }}
      >
        <Button
          onClick={requestClose}
          variant="tertiary"
          iconName="close"
          trackingLabel={(containerProps?.closeTrackingLabel ?? 'common-flyout-close') as TrackingLabel}
          style={{
            gridArea: 'headerClose',
            justifySelf: 'end',
            width: 'fit-content',
            ...noFocusStyle,
          }}
        />

        <Button
          onClick={requestClose}
          variant="tertiary"
          trackingLabel={(containerProps?.closeTrackingLabel ?? 'common-flyout-close') as TrackingLabel}
          style={{
            gridArea: 'actionClose',
            margin: '4px 0',
            ...noFocusStyle,
          }}
        >
          Close
        </Button>

        <CloseGuardContext.Provider value={closeGuardContext}>
          <Component {...(additionalProps ?? {})} />
        </CloseGuardContext.Provider>
      </Drawer>

      {children}
    </GlobalFlyoutContext.Provider>
  )
}
